import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import RealTimeClock from '../components/RealTimeClock';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Calendar, 
  Users, 
  FileText, 
  Globe, 
  Briefcase,
  ExternalLink,
  ArrowRight,
  Clock,
  MapPin,
  Package,
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../components/ui/hover-card';
import WidgetSidebar from '../components/WidgetSidebar';

const API = '/api';

const iconMap = {
  Mail, Calendar, Users, FileText, Globe, Briefcase,
  ExternalLink, ArrowRight, Clock
};

function BubbleLink({ bubble, index }) {
  const IconComponent = iconMap[bubble.icon] || Globe;
  
  return (
    <motion.a
      href={bubble.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl border border-[#E2E8F0] 
                 hover:border-[#16A34A] hover:shadow-md transition-all duration-300 group cursor-pointer aspect-square"
      data-testid={`bubble-${bubble.id}`}
    >
      <div className="w-16 h-16 rounded-full bg-[#F0FDF4] text-[#16A34A] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
        <IconComponent className="w-8 h-8" strokeWidth={1.5} />
      </div>
      <span className="text-lg font-semibold text-[#052E16] text-center group-hover:text-[#16A34A] transition-colors line-clamp-2 font-['Manrope']">
        {bubble.title}
      </span>
    </motion.a>
  );
}

function stripHtmlText(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function NewsPreview({ posts }) {
  if (!posts.length) {
    return (
      <div className="text-center py-8 text-[#57534E]">
        Žádné novinky
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.slice(0, 3).map((post, index) => (
        <motion.div
          key={post.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Link
            to={`/blog/${post.id}`}
            className="block p-4 rounded-xl bg-[#F8FAF8] border border-[#E2E8F0] hover:border-[#16A34A]/40 transition-all group"
            data-testid={`news-preview-${post.id}`}
          >
            <h4 className="font-semibold text-[#052E16] group-hover:text-[#16A34A] transition-colors line-clamp-1 font-['Manrope']">
              {post.title}
            </h4>
            <p className="text-sm text-[#57534E] mt-1 line-clamp-2">
              {stripHtmlText(post.content).substring(0, 100)}...
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-[#57534E]">
              <Clock className="w-3 h-3" />
              {new Date(post.created_at).toLocaleDateString('cs-CZ')}
            </div>
          </Link>
        </motion.div>
      ))}
      
      <Link
        to="/blog"
        className="flex items-center justify-center gap-2 py-3 text-[#16A34A] hover:text-[#15803D] transition-colors text-sm font-medium"
        data-testid="view-all-news"
      >
        Zobrazit všechny novinky
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

const EVENT_TYPE_CONFIG = {
  'Akce': { icon: CalendarDays, color: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]', dot: 'bg-[#3B82F6]' },
  'Deadline': { icon: AlertCircle, color: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]', dot: 'bg-red-500' },
  'Expedice': { icon: Package, color: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]', dot: 'bg-[#F59E0B]' },
};

function getEventType(summary) {
  if (summary.startsWith('Akce:')) return { type: 'Akce', label: summary.replace('Akce: ', '') };
  if (summary.startsWith('Deadline:')) return { type: 'Deadline', label: summary.replace('Deadline: ', '') };
  if (summary.startsWith('Expedice:')) return { type: 'Expedice', label: summary.replace('Expedice: ', '') };
  return { type: 'Akce', label: summary };
}

function EventDetailItem({ event }) {
  const { type, label } = getEventType(event.summary);
  const config = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['Akce'];
  const EventIcon = config.icon;
  const eventDate = new Date(event.start);
  const endDate = event.end ? new Date(event.end) : null;

  return (
    <div className={`p-3 rounded-xl border ${config.color}`} data-testid={`event-detail-${type.toLowerCase()}`}>
      <div className="flex items-start gap-2.5">
        <EventIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight">{label}</p>
          <div className="flex items-center gap-1.5 text-xs opacity-75 mt-1">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span>{eventDate.toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            {endDate && <span>— {endDate.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long' })}</span>}
          </div>
          {event.description && (
            <p className="text-xs opacity-60 mt-1.5 leading-relaxed">{event.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function CalendarDayCell({ day, dayEvents, isToday: isTodayDay, month, year }) {
  const hasEvents = dayEvents.length > 0;

  const cellContent = (
    <div
      className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm relative transition-all
        ${isTodayDay ? 'bg-[#16A34A] text-white font-bold shadow-sm' : hasEvents ? 'bg-[#F0FDF4] hover:bg-[#DCFCE7] ring-1 ring-[#16A34A]/10 cursor-pointer' : 'hover:bg-[#F8FAF8]'}
      `}
      data-testid={`calendar-day-${day}`}
    >
      <span className={`text-xs ${isTodayDay ? 'text-white' : 'text-[#1C1917]'}`}>{day}</span>
      {hasEvents && (
        <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
          {dayEvents.slice(0, 3).map((e, idx) => {
            const { type } = getEventType(e.summary);
            const config = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['Akce'];
            return <span key={idx} className={`w-2 h-2 rounded-full ${isTodayDay ? 'bg-white' : config.dot}`} />;
          })}
          {dayEvents.length > 3 && (
            <span className={`text-[8px] font-bold ${isTodayDay ? 'text-white/80' : 'text-[#57534E]'}`}>
              +{dayEvents.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (!hasEvents) return cellContent;

  const dayDate = new Date(year, month, day);
  const dayNames = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
  const monthNames = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        {cellContent}
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        sideOffset={8}
        className="w-80 bg-white border border-[#E2E8F0] shadow-xl rounded-xl p-0 overflow-hidden"
      >
        <div className="px-3 py-2 bg-[#F0FDF4] border-b border-[#E2E8F0]">
          <p className="text-xs font-semibold text-[#052E16]">
            {dayNames[dayDate.getDay()]} {day}. {monthNames[month]}
          </p>
          <p className="text-[10px] text-[#57534E]">{dayEvents.length} {dayEvents.length === 1 ? 'událost' : dayEvents.length < 5 ? 'události' : 'událostí'}</p>
        </div>
        <div className="p-2 space-y-1.5 max-h-48 overflow-y-auto">
          {dayEvents.map((e, idx) => {
            const { type, label } = getEventType(e.summary);
            const config = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['Akce'];
            const EventIcon = config.icon;
            return (
              <div key={idx} className={`p-2 rounded-lg border text-xs ${config.color}`}>
                <div className="flex items-center gap-1.5">
                  <EventIcon className="w-3 h-3 flex-shrink-0" />
                  <span className="font-medium truncate">{label}</span>
                </div>
                {e.description && <p className="opacity-60 mt-0.5 line-clamp-1 pl-[18px]">{e.description}</p>}
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function CalendarSection({ events }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const monthNames = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
  const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  const monthEvents = events.filter(e => {
    const d = new Date(e.start);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  const eventsByDay = {};
  monthEvents.forEach(e => {
    const day = new Date(e.start).getDate();
    if (!eventsByDay[day]) eventsByDay[day] = [];
    eventsByDay[day].push(e);
  });

  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Upcoming events — 60 day horizon
  const now = new Date();
  const horizon = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
  const allUpcoming = events
    .filter(e => new Date(e.start) >= now && new Date(e.start) <= horizon)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const visibleEvents = showAllEvents ? allUpcoming : allUpcoming.slice(0, 5);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-testid="calendar-section">
      {/* Calendar Grid */}
      <div className="lg:col-span-7">
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-[#052E16] font-['Manrope']">
                Kalendář
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={prevMonth} className="text-[#57534E] hover:text-[#16A34A]" data-testid="calendar-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-semibold text-[#052E16] min-w-[140px] text-center" data-testid="calendar-month-label">
                  {monthNames[month]} {year}
                </span>
                <Button variant="ghost" size="sm" onClick={nextMonth} className="text-[#57534E] hover:text-[#16A34A]" data-testid="calendar-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(d => (
                <div key={d} className="text-center text-xs font-semibold text-[#57534E] py-2">{d}</div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {Array.from({ length: startWeekday }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayEvents = eventsByDay[day] || [];
                return (
                  <div key={day} onClick={() => dayEvents.length > 0 && setSelectedDay(day)}>
                    <CalendarDayCell day={day} dayEvents={dayEvents} isToday={isToday(day)} month={month} year={year} />
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#E2E8F0]">
              <div className="flex items-center gap-1.5 text-xs text-[#57534E]">
                <span className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Akce
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#57534E]">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Deadline
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#57534E]">
                <span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> Expedice
              </div>
              <div className="ml-auto flex items-center gap-1 text-xs text-[#57534E]">
                <Info className="w-3 h-3" />
                <span>Najetím na den zobrazíte detail</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events List */}
      <div className="lg:col-span-5">
        <Card className="bg-white border-[#E2E8F0] shadow-sm h-full flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#16A34A]" />
                Nadcházející události
              </CardTitle>
              <span className="text-xs text-[#57534E] bg-[#F0FDF4] px-2 py-1 rounded-full" data-testid="events-count-badge">
                {allUpcoming.length} celkem
              </span>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {allUpcoming.length > 0 ? (
              <>
                <div className="space-y-2.5 flex-1">
                  {visibleEvents.map((event, index) => {
                    const { type, label } = getEventType(event.summary);
                    const config = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['Akce'];
                    const EventIcon = config.icon;
                    const eventDate = new Date(event.start);
                    const endDate = event.end ? new Date(event.end) : null;
                    const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className={`p-3 rounded-xl border ${config.color} group`}
                        data-testid={`upcoming-event-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <EventIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium leading-tight">{label}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs opacity-75">
                                {eventDate.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </span>
                              {endDate && (
                                <span className="text-xs opacity-60">
                                  — {endDate.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                            {event.description && (
                              <p className="text-xs opacity-60 mt-1 line-clamp-1">{event.description}</p>
                            )}
                          </div>
                          <span className="text-xs opacity-60 flex-shrink-0 whitespace-nowrap">
                            {daysUntil === 0 ? 'Dnes' : daysUntil === 1 ? 'Zítra' : `za ${daysUntil} dní`}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                {allUpcoming.length > 5 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllEvents(!showAllEvents)}
                    className="mt-3 w-full text-[#16A34A] hover:text-[#15803D] hover:bg-[#F0FDF4]"
                    data-testid="toggle-all-events"
                  >
                    {showAllEvents ? (
                      <><ChevronUp className="w-4 h-4 mr-1.5" /> Zobrazit méně</>
                    ) : (
                      <><ChevronDown className="w-4 h-4 mr-1.5" /> Zobrazit všech {allUpcoming.length} událostí</>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-[#57534E] text-sm">
                Žádné nadcházející události
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Day Detail Modal */}
      <Dialog open={selectedDay !== null} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="bg-white border-[#E2E8F0] rounded-2xl max-w-md" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#16A34A]" />
              {selectedDay && (() => {
                const d = new Date(year, month, selectedDay);
                const dNames = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
                return `${dNames[d.getDay()]} ${selectedDay}. ${monthNames[month]} ${year}`;
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="day-detail-events">
            {selectedDay && (eventsByDay[selectedDay] || []).map((event, idx) => (
              <EventDetailItem key={idx} event={event} />
            ))}
            {selectedDay && !(eventsByDay[selectedDay]?.length) && (
              <p className="text-sm text-[#57534E] text-center py-4">Žádné události</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [bubbles, setBubbles] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bubblesRes, blogRes, calRes] = await Promise.all([
          axios.get(`${API}/bubbles`, { withCredentials: true }),
          axios.get(`${API}/blog?limit=3`, { withCredentials: true }),
          axios.get(`${API}/calendar/events`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setBubbles(bubblesRes.data);
        setBlogPosts(blogRes.data);
        setCalendarEvents(calRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Layout>
      <div className="flex gap-8" data-testid="dashboard">
        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-8">
          {/* Welcome & Clock Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-8"
          >
            <h1 className="text-2xl md:text-3xl font-bold text-[#052E16] mb-6 font-['Manrope']">
              Vítejte, <span className="text-[#16A34A]">{user?.name}</span>
            </h1>
            <RealTimeClock />
          </motion.div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {/* Bubbles Section - Left Side */}
            <div className="lg:col-span-8">
              <Card className="bg-white border-[#E2E8F0] shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-[#052E16] font-['Manrope']">
                    Rozcestník
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="aspect-square rounded-2xl bg-[#F0FDF4] animate-pulse" />
                      ))}
                    </div>
                  ) : bubbles.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="bubbles-grid">
                      {bubbles.map((bubble, index) => (
                        <BubbleLink key={bubble.id} bubble={bubble} index={index} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-[#57534E]">
                      Žádné aplikace k zobrazení
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Side - News */}
            <div className="lg:col-span-4">
              <Card className="bg-white border-[#E2E8F0] shadow-sm h-full">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
                    Novinky
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-24 rounded-xl bg-[#F0FDF4] animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <NewsPreview posts={blogPosts} />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Calendar Section */}
          {calendarEvents.length > 0 && (
            <CalendarSection events={calendarEvents} />
          )}
        </div>

        {/* Widget Sidebar */}
        <div className="hidden xl:block w-80 flex-shrink-0">
          <div className="sticky top-4">
            <WidgetSidebar />
          </div>
        </div>
      </div>
    </Layout>
  );
}
